import { EnvironmentInjector, Injectable, runInInjectionContext } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { DataFile } from 'app/dynamic-form-builder/models/field.model';
import { Observable } from 'rxjs';
import { DbService } from './db.service';

/**
 * Clase de upload file
 */
export class Upload {
  /**
   * Identificador
   */
  $key!: string;
  /**
   * Archivo
   */
  file: File;
  /**
   * Nombre del archivo
   */
  name!: string;
  /**
   * URl para consumir el archivo
   */
  url!: string;
  /**
   * progreso
   */
  progress!: number;
  /**
   * Fecha de creacion
   */
  createdAt: Date = new Date();
  /**
   * Constructor
   *
   * @param file archivo
   */
  constructor(file: File) {
    this.file = file;
  }
}

/**
 * Servicio que maneja toda la carga de archivos al storage
 */
@Injectable({
  providedIn: 'root',
})
export class UploadService {
  /**
   * Referencia del storage
   */
  storageRef: any;
  /**
   * Archivos
   */
  public uploads!: Observable<Upload[]>;
  /**
   * Porcentaje de subida
   */
  uploadPercent!: Observable<number>;
  /**
   * URL de consumo
   */
  downloadURL!: Observable<string>;
  /**
   * Ruta master del storage
   */
  private basePath = '/uploads';


  /**
   * Constructor
   *
   * @param db conexion con BD
   * @param afStorage storage de firbase
   */
  constructor(
    private db: DbService,
    private afStorage: AngularFireStorage,
    private injector: EnvironmentInjector,
  ) { }

  /**
   * Sube un archivo al storage y actualiza el registro
   *
   * @param upload archivo a subir
   * @param data informacion del documento a subir
   */
  uploadDocument(
    upload: Upload,
    data: DataFile,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const path = `${this.basePath}/${data.routeStorage}/${upload.file.name}`;
      const { fileRef, task } = runInInjectionContext(this.injector, () => ({
        fileRef: this.afStorage.ref(path),
        task: this.afStorage.ref(path).put(upload.file),
      }));
      task.
        then(() => {
          runInInjectionContext(this.injector, () => fileRef.getDownloadURL()).subscribe((documentURL: string) => {
            upload.url = documentURL;
            upload.name = upload.file.name;
            data.type = upload.file.type;
            this.updateRegister(upload, data)
              .then((r) => resolve(documentURL))
              .catch((error: any) => reject(error));
          });
        });
    });
  }

  /**
   * Sube un archivo al storage
   *
   * @param upload archivo a subir
   * @param data informacion del documento a subir
   */
  uploadFile(
    upload: Upload,
    data: DataFile,
  ): Promise<any> {
    return new Promise((resolve) => {
      const path = `${this.basePath}/${data.routeStorage}/${upload.file.name}`;
      const { fileRef, task } = runInInjectionContext(this.injector, () => ({
        fileRef: this.afStorage.ref(path),
        task: this.afStorage.ref(path).put(upload.file),
      }));
      task.
        then(() => {
          runInInjectionContext(this.injector, () => fileRef.getDownloadURL()).subscribe((documentURL: string) => {
            upload.url = documentURL;
            upload.name = upload.file.name;
            data.type = upload.file.type;
            return resolve(documentURL);
          });
        });
    });
  }
  /**
   * Se encarga de actualizar el registro donde se subio un documento
   *
   * @param upload documento a subir
   * @param data informacion del documento a subir
   */
  private updateRegister(
    upload: Upload,
    info: DataFile
  ): Promise<any> {
    let data = {
      createdAt: new Date(),
      type: info.type,
      name: upload.name,
      url: upload.url,
    };
    if (info.extras) {
      data = Object.assign(info.extras, data);
    }
    return new Promise((resolve, reject) => {
      const collection = info.routeCollection ? info.routeCollection : '';
      this.db
        .add(collection, data)
        .then((r: any) => resolve(r))
        .catch((error: any) => {
          reject(error);
        });
    });
  }
}
